import { useEffect, useRef, useState, useCallback } from "react";
import { createUseStyles } from "react-jss";
import { ErrorBoundary } from "react-error-boundary";
import useResizeObserver from "use-resize-observer/polyfilled";
import clsx from "clsx";

import Content, { fetchMedia } from "./Content";
import { useWindowSize } from "../hooks";
import VPRPopup from "./VPRPopup";
import { getTheme } from "../Customisation";

const useStyles = createUseStyles(theme => ({
  "@keyframes enter": {
    "0%": {
      opacity: 0,
      width: 40,
      maxHeight: 40,
    },
    "30%": {
      opacity: 1,
      width: 400,
      maxHeight: 40,
    },
    "100%": {
      maxHeight: "100%",
    },
  },
  "@keyframes enter-large": {
    "0%": {
      opacity: 0,
      width: 40,
      maxHeight: 40,
    },
    "30%": {
      opacity: 1,
      width: props => (props.type === "gallery" ? "calc(100vw - 2px)" : "80vw"),
      maxHeight: 40,
    },
    "100%": {
      maxHeight: props => {
        switch (props.type) {
          case "video":
            return "100%";
          case "gallery":
            return "100%";
          default:
            return "80vh";
        }
      },
    },
  },
  "@keyframes exit": {
    from: { opacity: 1 },
    to: { opacity: 0 },
  },
  background: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    pointerEvents: props => (props.large ? "auto" : "none"),
    backgroundColor: props => props.large && "rgba(0, 0, 0, 0.3)",
    zIndex: 1000,
    "@media (max-width: 600px)": {
      zIndex: 20,
      pointerEvents: "auto !important",
      backgroundColor: "rgba(0, 0, 0, 0.3)",
    },
  },
  hidden: {
    display: "none",
  },
  popup: {
    position: "absolute",
    animation: "$enter 1.5s ease forwards",
    display: "flex",
    width: 400,
    overflow: "hidden",
    maxHeight: props => (props.fullscreen ? "100vh" : "calc(100vh - 50px)"),
    "@media (max-width: 600px)": {
      top: 1,
      left: 1,
      width: "calc(100vw - 3px) !important",
    },
  },
  lightbox: {
    extend: "popup",
    animationName: "$enter-large",
    maxHeight: "calc(100vh - 2px)",
    width: "calc(100vw - 2px)",
    "@media (max-width: 700px)": {
      width: "calc(100vw - 2px)",
    },
  },
  exit: {
    animation: "$exit 0.2s ease forwards",
    pointerEvents: "none",
  },
  close: {
    width: 40,
    height: 40,
    background: theme.popupPrimary,
    flexShrink: 0,
    flexGrow: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    pointerEvents: "auto",
    marginRight: getTheme().popupSpace ?? 1,
    "& span": {
      color: theme.textDefault,
      margin: 0,
      fontSize: 30,
      transform: "rotate(45deg)",
      userSelect: "none",
    },
  },
  vertical: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    pointerEvents: "auto",
    flexShrink: 0,
    overflowY: "auto", // TODO: set to hidden when in opening animation
    maxHeight: props => (props.large ? "100%" : "calc(100vh - 50px)"),
    maxWidth: "100vw",
    "& > *": {
      flexShrink: 0,
    },
  },
  header: {
    display: "flex",
    flexDirection: getTheme().popupHeaderDirection ?? "row",
    flexShrink: 0,
    justifyContent: "space-between",
  },
  title: {
    height: 40,
    background: theme.popupPrimary,
    color: theme.popUpHeaderText,
    flexShrink: 0,
    flexGrow: 1,
    padding: [6, 0, 0, 20],
    boxSizing: "border-box",
    fontSize: 20,
    fontWeight: "bolder",
    textAlign: "left",
    lineHeight: "initial",
  },
  expandedImageContainer: {
    width: "100%",
    height: "100%",
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    pointerEvents: "auto",
  },
  expandedImage: {
    height: "auto",
    width: "auto",
    maxWidth: "calc(100% - 2px)",
    maxHeight: "calc(100% - 2px)",
  },
  unfullscreen: {
    position: "absolute",
    top: 5,
    right: 5,
    width: 30,
    height: 30,
    cursor: "pointer",
  },
}));

const Popups = ({
  setUIVisibility = () => { },
  setAudioClip,
  player,
  fullscreen = false,
  floorplanOptions,
}) => {
  const [hidden, setHidden] = useState(true);
  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [expandedImage, setExpandedImage] = useState(null);
  const [config, setConfig] = useState(null);
  const { width, height } = useWindowSize();
  const {
    ref: popupRef,
    width: elementWidth = 0,
    height: elementHeight = 0,
  } = useResizeObserver();
  const onTransitionRef = useRef(null);
  const timeoutRef = useRef();
  
  const large =
    (!!config &&
      (["web", "gallery"].includes(config.type) || config.options?.large)) ||
    !!expandedImage;
  const classes = useStyles({
    large,
    type: config?.type,
    fullscreen,
  });

  const isTimeline = !!config && config.type === "timeline";
  const isMobile = width < 600;

  useEffect(() => {
    if (open) {
      if (isMobile) {
        setUIVisibility(false);
      } else {
        setUIVisibility(!large);
      }
    } else {
      if (large) {
        setUIVisibility(true);
      }
    }
  }, [open, setUIVisibility, isMobile, large]);

  useEffect(() => {
    const openURL = url => {
      if (url.indexOf("https://") < 0 && url.indexOf("http://") < 0) {
        url = `http://${url}`;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    };

    const isTag = ({ title, content, image }) => !title && !content && !image;

    if (player) {
      player.popupOverride = true;
      player.onPopupMove = coordinates => setPos(coordinates);
      player.onPopupOpen = config => {
        // hide current popup
        setOpen(false);
        // bunch of logic to do stuff instead of opening the popup
        if (config.type === "web" && config.options.display !== "lightbox") {
          openURL(config.options.url);
        } else if (
          (config.type === "note" && isTag(config.options)) ||
          config.options.tag
        ) {
          player.activePopup.setInactive();
          player.activePopup = null;
          setConfig(null);
        } else if (config.type === "audio" && !config.options.content) {
          if (config.options.clip) {
            fetchMedia(config.options.clip.id)
              .then(data =>
                setAudioClip({ url: data.public_url, name: data.name }),
              )
              .catch(e => console.error(e));
          } else {
            setAudioClip({
              url: config.options.url,
              name: config.options.title || config.tooltip,
            });
          }
          player.activePopup.setInactive();
          player.activePopup = null;
          setConfig(null);
        } else {
          // hacky way of delaying until next render
          setTimeout(() => {
            setOpen(true);
            setConfig({
              ...config,
              start: floorplanOptions?.stacks?.[0]?.start,
            });
          });
        }
      };
      onTransitionRef.current = player.addOnTransitionFunction(() => {
        if (!isTimeline) setOpen(false);
      });

      return () => {
        player.popupOverride = false;
        player.removeOnTransitionFunction(onTransitionRef.current);
      };
    }
  }, [player, width, height, setAudioClip, isTimeline, floorplanOptions]);

  useEffect(() => {
    if (open) {
      setHidden(false);
    } else {
      timeoutRef.current = setTimeout(() => setHidden(true), 200);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [player, open]);

  useEffect(() => {
    if (hidden && player) {
      player.activePopup = null;
      setConfig(null);
    }
  }, [hidden, player]);

  const close = useCallback(
    (e, fromEvent = false) => {
      if (expandedImage) {
        setExpandedImage(null);
      } else {
        setOpen(false);
        if (player && player.activePopup) player.activePopup.setInactive();
        if (
          !fromEvent &&
          typeof window !== "undefined" &&
          !!window.GlobalEvent
        ) {
          window.GlobalEvent.emit("SET_INACTIVE_POPUP");
        }
      }
    },
    [player, expandedImage],
  );

  // #Shared Tour Code
  useEffect(() => {
    if (typeof window !== "undefined" && !!window.GlobalEvent) {
      window.GlobalEvent.on("INNER_SET_INACTIVE_POPUP", () => {
        close(undefined, true);
      });
    }
  }, [close]);

  // get coordinates for the popup - returns a style object
  const getCoords = () => {
    if (config === null || isMobile) return {};
    if (large || pos.x === null || pos.y === null) {

      // embedded tours in iframes get a slight blur if "transform" css is set on the lightbox container
      // when the "enter" animation completes on fullscreen iframes, we assume the lightbox is at top: 0, left: 0
      // so the transformation can be removed, to remove the blur.
      if (large && finished) return {};

      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%,-50%)",
      };
    } else {
      // calculate position properly
      // -20 so that X is directly over the hotspot
      const coords = {
        top: pos.y - 20,
        left: pos.x - 20,
      };

      // check x axis
      if (coords.left < 0) {
        coords.left = 0;
      } else if (coords.left + elementWidth > width) {
        coords.left = width - elementWidth;
      }

      // check y axis
      if (coords.top < 0) {
        coords.top = 0;
      } else if (coords.top + elementHeight > height - 51) {
        coords.top = Math.max(height - elementHeight - 51, 0);
      }

      return coords;
    }
  };

  if (hidden) return null;

  return (
    <div
      className={clsx(classes.background, { [classes.hidden]: hidden })}
      onClick={close} // pointer events only enabled for web
    >
      {config &&
        (config.options.title !== "NOTE-FOR-VIDEO" ? (
          <div
            className={clsx(large ? classes.lightbox : classes.popup, {
              [classes.exit]: !open,
            })}
            ref={popupRef}
            style={getCoords()}
            onAnimationStart={() => { setFinished(false); }}
            onAnimationEnd={() => { setFinished(true); }}
          >
            <div
              className={classes.vertical}
              onClick={evt => evt.stopPropagation()}
            >
              <div className={classes.header}>
                <div className={classes.close} onClick={close} role="button">
                  <span>+</span>
                </div>
                <div className={classes.title}>
                  {config.options.title || config.tooltip}
                </div>
              </div>
              <ErrorBoundary fallback={<p>Error in popup content</p>}>
                <Content
                  config={expandedImage ? expandedImage.config : config}
                  setExpandedImage={setExpandedImage}
                  setAudioClip={setAudioClip}
                  player={player}
                  large={large}
                />
              </ErrorBoundary>
            </div>
          </div>
        ) : (
          <VPRPopup style={getCoords()} closePopup={close} />
        ))}
    </div>
  );
};

export default Popups;
